// Zone 2 — account, no card. Collects the three fields the questionnaire never asked
// (firstName, lastName, dateOfBirth — the API requires them) with the chosen plan held
// in view beside the form so it doesn't feel like a toll gate. No payment anywhere.
import { useState } from 'react';
import type { Answers } from '../lib/session';
import type { Coverage } from '../lib/api';
import { createAccount, RateLimited, ApiError } from '../lib/api';
import { useIsNarrow } from '../lib/useIsNarrow';
import { PrimaryButton, card } from '../components/ui';
import { track } from '../lib/analytics';
import { HeldNotice } from '../components/HeldNotice';
import type { ExistingReason } from './ExistingAccount';

export function Account({
  answers,
  coverage,
  onCreated,
  onAlreadyConverted,
  onExisting,
}: {
  answers: Answers;
  coverage: Coverage | null;
  onCreated: (claimLink?: string) => void;
  // Same session submitted twice (back button, reload): account + invite already exist.
  onAlreadyConverted: () => void;
  // 'existing': the server found an account for this email. 'login': the user asked.
  onExisting: (reason: ExistingReason) => void;
}) {
  const narrow = useIsNarrow();
  const [firstName, setFirst] = useState('');
  const [lastName, setLast] = useState('');
  const [dob, setDob] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid = firstName.trim() && lastName.trim() && dob;

  async function submit() {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await createAccount({ firstName: firstName.trim(), lastName: lastName.trim(), dateOfBirth: dob });
      if (res.alreadyConverted) {
        // The conversion was counted the first time; just replay the hand-off.
        onAlreadyConverted();
        return;
      }
      if (res.existing) {
        // Known email: the server linked this intake to their account and sent no
        // email, so neither the created event nor "check your email" applies.
        onExisting('existing');
        return;
      }
      track('account_created');
      onCreated(res.invite?.claimLink);
    } catch (e) {
      if (e instanceof RateLimited) setError('One sec, try again in a moment.');
      else if (e instanceof ApiError && e.status === 0) setError('Connection issue. Check your network and try again.');
      else setError('Something went wrong creating your account. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  const planName = coverage?.planName || coverage?.interimPlanName || 'Your recovery plan';
  // Post-op guard: no plan is waiting yet, a person sets it up (see HeldNotice).
  const held = coverage?.hold === 'recent-surgery';
  const country = (answers.country as string) || 'US';
  const goal = (answers.goal as string) || null;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)' }}>
      <div style={{ maxWidth: 920, margin: '0 auto', padding: narrow ? '28px 20px 48px' : '56px 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1.2fr 1fr', gap: 24, alignItems: 'start' }}>
          {/* Form */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--teal-deep)', textTransform: 'uppercase', letterSpacing: '.14em' }}>
              Questionnaire complete
            </div>
            <div className="pretty" style={{ fontSize: narrow ? 28 : 34, fontWeight: 800, lineHeight: 1.16, marginTop: 10 }}>{held ? 'Create your account.' : 'Save your plan.'}</div>
            <div className="pretty" style={{ fontSize: 15, fontWeight: 500, color: 'var(--slate)', marginTop: 10, lineHeight: 1.5 }}>
              {held ? 'So we can set up your plan with you.' : 'So it’s waiting for you when you open the app.'} No card, no charge. You’ll set a password from the email we send next.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 24 }}>
              <Field label="First name" value={firstName} onChange={setFirst} autoComplete="given-name" />
              <Field label="Last name" value={lastName} onChange={setLast} autoComplete="family-name" />
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate)', marginBottom: 6 }}>Date of birth</div>
                <div style={{ ...card, padding: '4px 8px' }}>
                  <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} style={{ border: 'none', outline: 'none', width: '100%', padding: '12px', fontSize: 16, fontWeight: 700, background: 'transparent' }} />
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--slate)', marginTop: 5 }}>Your plan adjusts for age.</div>
              </div>
            </div>

            <div style={{ marginTop: 24 }}>
              <PrimaryButton full disabled={!valid || busy} onClick={submit}>
                {busy ? 'Creating your account…' : 'Create my account'}
              </PrimaryButton>
              {error && <div style={{ fontSize: 13, fontWeight: 700, color: '#C2453F', marginTop: 10 }}>{error}</div>}
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--slate)', marginTop: 14, textAlign: 'center' }}>
                No payment details. Already have an account?{' '}
                <button onClick={() => onExisting('login')} style={{ color: 'var(--teal-deep)', fontWeight: 800 }}>Log in instead</button>
              </div>
            </div>
          </div>

          {/* Plan held in view */}
          <div style={{ ...card, borderRadius: 16, padding: 22, background: '#fff' }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--teal-deep)', textTransform: 'uppercase', letterSpacing: '.12em' }}>
              {held ? 'What happens next' : 'Waiting for you'}
            </div>
            {held ? (
              <div style={{ marginTop: 14 }}>
                <HeldNotice country={country} />
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginTop: 14 }}>
                <div style={{ background: 'var(--teal-tint)', borderRadius: 12, padding: '10px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--teal-deep)', lineHeight: 1 }}>12</div>
                  <div style={{ fontSize: 9, fontWeight: 800, color: 'var(--teal-deep)', letterSpacing: '.1em' }}>WEEKS</div>
                </div>
                <div style={{ fontSize: 16, fontWeight: 800 }}>{planName}</div>
              </div>
            )}
            {goal && (
              <div style={{ borderTop: '1px solid #EEF1F2', marginTop: 16, paddingTop: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--teal-deep)', textTransform: 'uppercase', letterSpacing: '.12em' }}>Your goal</div>
                <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4 }}>{goal}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, autoComplete }: { label: string; value: string; onChange: (v: string) => void; autoComplete?: string }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate)', marginBottom: 6 }}>{label}</div>
      <div style={{ ...card, padding: '4px 8px' }}>
        <input value={value} autoComplete={autoComplete} onChange={(e) => onChange(e.target.value)} style={{ border: 'none', outline: 'none', width: '100%', padding: '12px', fontSize: 16, fontWeight: 600, background: 'transparent' }} />
      </div>
    </div>
  );
}
